import { useState } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createClient } from '@/lib/supabase/client'
import { GuestProfile } from '@/components/guests/GuestProfile'
import { GuestModal } from '@/components/guests/GuestModal'
import { AddNoteModal } from '@/components/guests/AddNoteModal'
import { InviteToEpisodeModal } from '@/components/guests/InviteToEpisodeModal'
import { EmailComposeModal } from '@/components/guests/EmailComposeModal'
import { useCanDeleteGuest, useDeleteGuest } from '@/hooks/useGuests'
import { useSendCustomEmail } from '@/hooks/useEmail'
import type { Appearance, Communication } from '@/types'
import { useLabels, singular } from '@/lib/terminology'

interface GuestData {
  id: string
  name: string
  title: string
  company: string | null
  email: string | null
  phone: string | null
  bio: string | null
  location: string | null
  avatar_url: string | null
  status: string
  tags: string[] | null
  notes: string | null
  social_links: {
    twitter?: string
    linkedin?: string
    website?: string
  } | null
}

interface AppearanceData {
  id: string
  stage: string
  episodes: {
    id: string
    number: number
    title: string
    recording_date: string
    air_date: string
  }
}

interface CommunicationData {
  id: string
  guest_id: string
  appearance_id: string | null
  type: 'email' | 'note' | 'call'
  subject: string | null
  content: string
  direction: 'inbound' | 'outbound' | null
  sent_by: string | null
  created_at: string
}

export function GuestDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const supabase = createClient()
  const queryClient = useQueryClient()
  const labels = useLabels()

  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false)
  const [isEmailComposeOpen, setIsEmailComposeOpen] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)

  // Check if guest can be deleted
  const { data: deletability } = useCanDeleteGuest(id)
  const deleteGuest = useDeleteGuest()
  const sendCustomEmail = useSendCustomEmail()

  // Fetch guest data
  const { data: guest, isLoading: guestLoading, refetch: refetchGuest } = useQuery({
    queryKey: ['guest', id],
    queryFn: async () => {
      if (!id) return null

      const { data, error } = await supabase
        .from('guests')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error
      return data as GuestData
    },
    enabled: !!id,
  })

  // Fetch appearances for this guest
  const { data: appearances = [] } = useQuery({
    queryKey: ['guest-appearances', id],
    queryFn: async () => {
      if (!id) return []

      const { data, error } = await supabase
        .from('appearances')
        .select(`
          id,
          stage,
          episodes (
            id,
            number,
            title,
            recording_date,
            air_date
          )
        `)
        .eq('guest_id', id)
        .order('created_at', { ascending: false })

      if (error) throw error
      return (data as AppearanceData[]) || []
    },
    enabled: !!id,
  })

  // Fetch communications for this guest
  const { data: communicationsData = [] } = useQuery({
    queryKey: ['communications', id],
    queryFn: async (): Promise<CommunicationData[]> => {
      if (!id) return []

      const { data, error } = await supabase
        .from('communications')
        .select('*')
        .eq('guest_id', id)
        .order('created_at', { ascending: false })

      if (error) throw error
      return (data as CommunicationData[]) || []
    },
    enabled: !!id,
  })

  // Update guest notes mutation
  const updateNotes = useMutation({
    mutationFn: async (notes: string) => {
      if (!id) return

      const { error } = await supabase
        .from('guests')
        .update({ notes } as never)
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guest', id] })
      setIsAddNoteModalOpen(false)
    },
  })

  if (guestLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-6 w-32 bg-zinc-200 dark:bg-zinc-800 rounded mb-4" />
          <div className="flex gap-4">
            <div className="w-20 h-20 bg-zinc-200 dark:bg-zinc-800 rounded-full" />
            <div className="flex-1">
              <div className="h-8 w-48 bg-zinc-200 dark:bg-zinc-800 rounded mb-2" />
              <div className="h-4 w-64 bg-zinc-200 dark:bg-zinc-800 rounded" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!guest) {
    return (
      <div className="p-6 text-center">
        <p className="text-zinc-500 dark:text-zinc-400">{singular(labels.guests)} not found</p>
        <button
          onClick={() => navigate('/guests')}
          className="mt-4 text-primary-500 hover:text-primary-600"
        >
          Back to {labels.guests}
        </button>
      </div>
    )
  }

  // Transform data for GuestProfile component
  const guestProfile = {
    id: guest.id,
    name: guest.name,
    title: guest.title,
    company: guest.company || undefined,
    email: guest.email || undefined,
    phone: guest.phone || undefined,
    bio: guest.bio || undefined,
    location: guest.location || undefined,
    avatarUrl: guest.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(guest.name)}&background=6369D1&color=fff`,
    status: guest.status as 'active' | 'archived',
    tags: guest.tags || undefined,
    socialLinks: guest.social_links || undefined,
    appearanceCount: appearances.length,
    lastAppearance: appearances.length > 0 ? appearances[0].episodes?.recording_date : undefined,
  }

  const transformedAppearances: Appearance[] = appearances.map((a) => ({
    id: a.id,
    stage: a.stage as Appearance['stage'],
    recordingDate: a.episodes?.recording_date || '',
    episodeNumber: a.episodes?.number || 0,
    episodeTitle: a.episodes?.title || 'Untitled',
    airDate: a.episodes?.air_date || '',
  }))

  // Transform communications data for GuestProfile component
  const communications: Communication[] = communicationsData.map((c) => ({
    id: c.id,
    type: c.type as Communication['type'],
    subject: c.subject || undefined,
    content: c.content,
    direction: (c.direction ?? undefined) as Communication['direction'],
    date: c.created_at,
  }))

  const handleSendEmail = async (data: { subject: string; body: string }) => {
    if (!guest?.id || !guest?.email) return

    setEmailError(null)
    try {
      await sendCustomEmail.mutateAsync({
        guest_id: guest.id,
        subject: data.subject,
        body: data.body,
      })
      setIsEmailComposeOpen(false)
      queryClient.invalidateQueries({ queryKey: ['communications', id] })
    } catch (error) {
      setEmailError(error instanceof Error ? error.message : 'Failed to send email')
    }
  }

  const handleArchiveGuest = async () => {
    if (!guest) return

    const newStatus = guest.status === 'archived' ? 'active' : 'archived'
    const { error } = await supabase
      .from('guests')
      .update({ status: newStatus } as never)
      .eq('id', guest.id)

    if (error) {
      console.error('Failed to archive guest:', error)
      return
    }

    refetchGuest()
    queryClient.invalidateQueries({ queryKey: ['guests'] })
    queryClient.invalidateQueries({ queryKey: ['pipeline'] })
  }

  const handleDeleteGuest = async () => {
    if (!guest || !deletability?.canDelete) return
    setShowDeleteConfirm(true)
  }

  const confirmDeleteGuest = async () => {
    if (!guest) return

    try {
      await deleteGuest.mutateAsync(guest.id)
      navigate('/guests')
    } catch (error) {
      console.error('Failed to delete guest:', error)
    } finally {
      setShowDeleteConfirm(false)
    }
  }

  return (
    <>
      <GuestProfile
        guest={guestProfile}
        appearances={transformedAppearances}
        communications={communications}
        notes={guest.notes || undefined}
        canDelete={deletability?.canDelete ?? false}
        deleteReason={deletability?.reason}
        isDeleting={deleteGuest.isPending}
        onBack={() => navigate('/guests')}
        onEditGuest={() => setIsEditModalOpen(true)}
        onArchiveGuest={handleArchiveGuest}
        onDeleteGuest={handleDeleteGuest}
        onAddNote={() => setIsAddNoteModalOpen(true)}
        onSendEmail={() => {
          if (guest.email) {
            setEmailError(null)
            setIsEmailComposeOpen(true)
          }
        }}
        onAppearanceClick={(appearanceId) => {
          const appearance = appearances.find((a) => a.id === appearanceId)
          if (appearance?.episodes?.id) {
            navigate(`/recordings/${appearance.episodes.id}`)
          }
        }}
        onInviteToEpisode={() => setShowInviteModal(true)}
      />

      <GuestModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        guest={{
          id: guest.id,
          name: guest.name,
          title: guest.title,
          company: guest.company || '',
          email: guest.email || '',
        }}
        onSuccess={() => refetchGuest()}
      />

      <AddNoteModal
        isOpen={isAddNoteModalOpen}
        onClose={() => setIsAddNoteModalOpen(false)}
        currentNotes={guest.notes || undefined}
        onSave={(notes) => updateNotes.mutate(notes)}
        isPending={updateNotes.isPending}
      />

      <InviteToEpisodeModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        guestId={guest.id}
        guestName={guest.name}
        guestEmail={guest.email}
        existingEpisodeIds={appearances.map((a) => a.episodes?.id).filter(Boolean) as string[]}
      />

      <EmailComposeModal
        isOpen={isEmailComposeOpen}
        onClose={() => {
          setIsEmailComposeOpen(false)
          setEmailError(null)
        }}
        recipientEmail={guest.email || ''}
        recipientName={guest.name}
        onSend={handleSendEmail}
        isPending={sendCustomEmail.isPending}
        error={emailError}
      />

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowDeleteConfirm(false)}
            data-testid="delete-confirm-backdrop"
          />
          <div
            className="relative w-full max-w-md mx-4 bg-white dark:bg-zinc-900 rounded-xl shadow-xl p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="delete-confirm-modal"
          >
            <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 mb-2">
              Delete Guest
            </h3>
            <p className="text-zinc-600 dark:text-zinc-400 mb-4">
              Are you sure you want to permanently delete {guest.name}? This action cannot be undone.
            </p>
            <p className="text-sm text-zinc-500 dark:text-zinc-500 mb-6">
              This guest has not accepted any invitations, so deleting them will free up a guest slot in your account.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors"
                data-testid="delete-confirm-cancel"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteGuest}
                disabled={deleteGuest.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-rose-600 hover:bg-rose-700 rounded-lg transition-colors disabled:opacity-50"
                data-testid="delete-confirm-button"
              >
                {deleteGuest.isPending ? 'Deleting...' : 'Delete Guest'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
